import { ScimReference } from './ReferenceTypes';

export type EntityType = 'Person' | 'Student' | 'Contractor' | 'Public' | 'Shared' | 'Employee' | 'Sponsored' | 'Applicant' | 'User' | 'Vacancy' | 'Group' | 'School' | 'Building' | 'Class' | 'ResponsibilityCenter' | 'Image' | 'Base';

// Helper function to safely extract string value from EntityType (which can be string or object)
export const getEntityTypeString = (entityType: any): string => {
    if (!entityType) return '';
    if (typeof entityType === 'string') return entityType;
    if (typeof entityType === 'object') {
        // Prioritize DisplayName for entity type objects (most user-friendly)
        if (entityType.DisplayName) return entityType.DisplayName;
        // Fallback to other common properties
        if (entityType.name) return entityType.name;
        if (entityType.Type) return entityType.Type;
        if (entityType.type) return entityType.type;
        if (entityType.Id) return entityType.Id;
        if (entityType.id) return entityType.id;
    }
    return String(entityType);
};

// Schema definitions based on schemas.json
export interface ScimSchema {
    id: string;
    name: string;
    attributes: ScimAttribute[];
    schemas?: string[];
    meta?: {
        resourceType: string;
        created?: string;
        lastModified?: string;
    };
}

export interface ScimAttribute {
    name: string;
    type: string;
    mutability: string;
    required: boolean;
    returned: string;
    uniqueness: string;
    multiValued: boolean;
    subAttributes: ScimAttribute[];
    caseExact?: boolean;
    referenceTypes?: string[];
    canonicalValues?: string[];
}

// Base SCIM user interface with common fields
export interface BaseScimUser {
    Id: string;
    UserName?: string;
    DisplayName?: string;
    schemas?: string[];

    // Core SCIM fields
    emails?: Array<{
        value: string;
        primary?: boolean;
        type?: string;
        display?: string;
    }>;
    name?: {
        givenName?: string;
        familyName?: string;
        formatted?: string;
    };
    active?: boolean;
    meta?: {
        resourceType: string;
        created?: string;
        lastModified?: string;
        version?: string;
    };

    // DCPS-specific fields (common across entity types) - using PascalCase from schemas.json
    AccountName?: string;
    EntraId?: string;
    OnPremId?: string;
    UserPrincipalName?: string;
    FirstName?: string;
    LastName?: string;
    MiddleInitial?: string;
    Email?: string;
    PersonalEmail?: string;
    SecondaryEmail?: string;
    AcademicRole?: string;
    JobCode?: string;
    JobTitle?: string;
    Manager?: ScimReference;
    MemberOf?: string[];
    MemberOfTransitive?: string[];
    Rc?: ScimReference;
    Rcs?: ScimReference[];
    Role?: string;
    School?: ScimReference;
    Schools?: ScimReference[];
    Sections?: string[] | Array<{ Id: string; DisplayName: string; Course?: string }>;
    Sponsor?: ScimReference;
    Sponsors?: string[];
    Type?: string;
    GradeLevel?: string;
    GradeLevels?: string[];
    Classes?: string[] | Array<{ Id: string; DisplayName: string; Subject?: string }>;
    PerpetualAccess?: string[];
    WeekendAccess?: string[];
    WorkdayAccess?: string[];
    CardData?: string;
    EmployeeNumber?: string;
    SapUserId?: string;
    Dob?: string;
    EndDate?: string;
    StartDate?: string;
    Guardians?: string[];
    SchoolName?: string;
    Teachers?: string[];
    PrincipalOf?: string[];
    SchoolPrincipalOf?: string[];
    BuildingAssistant?: string[];
    BuildingManager?: string[];
    Manages?: string[];
    OfficePhone?: string;
    Address?: string;
    Apt?: string;
    City?: string;
    State?: string;
    Zip?: string;
    MobilePhone?: string;
    PhoneIsVerified?: boolean;
    PhoneLastVerified?: string;
    PhoneNumberVerified?: string;
    SendSms?: boolean;
    Students?: string[];
    Version?: number;
    Checksum?: number;
    Hash?: string;
    Recorded?: string;
    VersionId?: string;
}

// Student-specific interface
export interface StudentUser extends BaseScimUser {
    EntityType: 'Student';
    // Student-specific fields (matching API response format)
    // Note: These are already in PascalCase from the base interface
}

// Contractor-specific interface
export interface ContractorUser extends BaseScimUser {
    EntityType: 'Contractor';
    // Contractor-specific fields (matching API response format)
    // Note: These are already in PascalCase from the base interface
}

// Public-specific interface
export interface PublicUser extends BaseScimUser {
    EntityType: 'Public';
    // Public-specific fields (matching API response format)
    // Note: These are already in PascalCase from the base interface
}

// Shared-specific interface
export interface SharedUser extends BaseScimUser {
    EntityType: 'Shared';
    // Shared-specific fields (matching API response format)
    // Note: These are already in PascalCase from the base interface
}

// Employee-specific interface
export interface EmployeeUser extends BaseScimUser {
    EntityType: 'Employee';
    // Employee-specific fields (matching API response format)
    // Note: These are already in PascalCase from the base interface
}

// Sponsored-specific interface
export interface SponsoredUser extends BaseScimUser {
    EntityType: 'Sponsored';
    // Sponsored-specific fields
    // Note: These are already in PascalCase from the base interface
}

// Vacancy-specific interface
export interface VacancyPerson extends BaseScimUser {
    EntityType: 'Vacancy';
    // Vacancy-specific fields based on Vacancy.ts
    // Note: These are already in PascalCase from the base interface
}

// Applicant-specific interface
export interface ApplicantUser extends BaseScimUser {
    EntityType: 'Applicant';
    // Applicant-specific fields
    AcademicRole?: string;
    ApplicationId?: string;
    ApplicationStatus?: string;
    ApplicationDate?: string;
}

// User-specific interface (general user type)
export interface UserUser extends BaseScimUser {
    EntityType: 'User';
    // User-specific fields
    // Note: These are already in PascalCase from the base interface
}

export interface GroupUser extends BaseScimUser {
    EntityType: 'Group';
    // Group-specific fields
    Description?: string;
    Member?: string[];
    MemberTransitive?: string[];
    Scope?: string;
    // Note: ObjectId is already in BaseScimUser
}

export interface SchoolUser extends BaseScimUser {
    EntityType: 'School';
    // School-specific fields
    PhoneNumber?: string;
    Principals?: string[];
    Region?: string;
    RegionLabel?: string;
}

export interface BuildingUser extends BaseScimUser {
    EntityType: 'Building';
    // Building-specific fields
    Assistant?: string;
    BackupAssistant?: string;
    BackupManager?: string;
    District?: boolean;
}

export interface ClassUser extends BaseScimUser {
    EntityType: 'Class';
    // Class-specific fields
    Attendance?: boolean;
    Course?: string;
    CoursePeriods?: string[];
    EndDate?: string;
    Gpa?: boolean;
    Grades?: boolean;
    Labels?: string[];
    MarkingPeriods?: string[];
    Period?: string;
    Room?: string;
    StudentCount?: number;
    Students?: string[];
    Subject?: string;
    Subjects?: string[];
    Teacher?: string;
    Teachers?: string[];
    Vacant?: boolean;
}

export interface ResponsibilityCenterUser extends BaseScimUser {
    EntityType: 'ResponsibilityCenter';
    // ResponsibilityCenter-specific fields
    Fax?: string;
    Ids?: string[];
    Managers?: string[];
    PhoneNumber?: string;
    Principals?: string[];
}

export interface ImageUser extends BaseScimUser {
    EntityType: 'Image';
    // Image-specific fields
    Error?: string;
    HasError?: boolean;
    Size?: number;
    Thumbnail?: string;
    Owner?: string[];
}

export interface PersonUser extends BaseScimUser {
    EntityType: 'Person';
    // Person-specific fields - this is the unified schema that includes all user attributes
    // All fields from BaseScimUser are available
}

export interface BaseUser extends BaseScimUser {
    EntityType: 'Base';
    // Base-specific fields
    Checksum?: number;
    Recorded?: string;
    VersionId?: string;
}

// Discriminated union type for all SCIM users
export type ScimUser = PersonUser | StudentUser | ContractorUser | PublicUser | SharedUser | EmployeeUser | SponsoredUser | VacancyPerson | ApplicantUser | UserUser | GroupUser | SchoolUser | BuildingUser | ClassUser | ResponsibilityCenterUser | ImageUser | BaseUser;

// Type guards for checking entity types
export const isPersonUser = (user: ScimUser): user is PersonUser => user.EntityType === 'Person';
export const isStudentUser = (user: ScimUser): user is StudentUser => user.EntityType === 'Student';
export const isContractorUser = (user: ScimUser): user is ContractorUser => user.EntityType === 'Contractor';
export const isPublicUser = (user: ScimUser): user is PublicUser => user.EntityType === 'Public';
export const isSharedUser = (user: ScimUser): user is SharedUser => user.EntityType === 'Shared';
export const isEmployeeUser = (user: ScimUser): user is EmployeeUser => user.EntityType === 'Employee';
export const isSponsoredUser = (user: ScimUser): user is SponsoredUser => user.EntityType === 'Sponsored';
export const isVacancyPerson = (user: ScimUser): user is VacancyPerson => user.EntityType === 'Vacancy';
export const isApplicantUser = (user: ScimUser): user is ApplicantUser => user.EntityType === 'Applicant';
export const isUserUser = (user: ScimUser): user is UserUser => user.EntityType === 'User';
export const isGroupUser = (user: ScimUser): user is GroupUser => user.EntityType === 'Group';
export const isSchoolUser = (user: ScimUser): user is SchoolUser => user.EntityType === 'School';
export const isBuildingUser = (user: ScimUser): user is BuildingUser => user.EntityType === 'Building';
export const isClassUser = (user: ScimUser): user is ClassUser => user.EntityType === 'Class';
export const isResponsibilityCenterUser = (user: ScimUser): user is ResponsibilityCenterUser => user.EntityType === 'ResponsibilityCenter';
export const isImageUser = (user: ScimUser): user is ImageUser => user.EntityType === 'Image';
export const isBaseUser = (user: ScimUser): user is BaseUser => user.EntityType === 'Base';

// Helper function to get entity type with fallback
export const getEntityType = (user: ScimUser): EntityType => {
    if (!user) return 'User';

    const entityType = (user as any).EntityType;
    if (!entityType) return 'User';

    // If EntityType is a string, return it directly
    if (typeof entityType === 'string') {
        return entityType as EntityType;
    }

    // If EntityType is a reference object, extract the DisplayName or Id
    if (typeof entityType === 'object' && entityType !== null) {
        if (entityType.DisplayName) return entityType.DisplayName as EntityType;
        if (entityType.Id) return entityType.Id as EntityType;
        if (entityType.value) return entityType.value as EntityType;
    }

    // Fallback to 'User' if we can't determine the type
    return 'User';
};

export interface CreateUserData {
    userName: string;
    displayName?: string;
    email: string;
    firstName?: string;
    lastName?: string;
}

export interface UpdateUserData {
    userName?: string;
    displayName?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
}

// Photo response type
export interface PhotoResponse {
    Photo?: string; // Base64 encoded image
}

// Schema mapping for entity types
export const ENTITY_SCHEMAS: Record<EntityType, string> = {
    'Person': 'urn:dcps:scim:v1:Person',
    'Student': 'urn:dcps:scim:v1:Student',
    'Contractor': 'urn:dcps:scim:v1:Contractor',
    'Public': 'urn:dcps:scim:v1:Public',
    'Shared': 'urn:dcps:scim:v1:Shared',
    'Employee': 'urn:dcps:scim:v1:Employee',
    'Sponsored': 'urn:dcps:scim:v1:Sponsored',
    'Applicant': 'urn:dcps:scim:v1:Applicant',
    'User': 'urn:dcps:scim:v1:User',
    'Vacancy': 'urn:dcps:scim:v1:Vacancy',
    'Group': 'urn:dcps:scim:v1:Group',
    'School': 'urn:dcps:scim:v1:School',
    'Building': 'urn:dcps:scim:v1:Building',
    'Class': 'urn:dcps:scim:v1:Class',
    'ResponsibilityCenter': 'urn:dcps:scim:v1:ResponsibilityCenter',
    'Image': 'urn:dcps:scim:v1:Image',
    'Base': 'urn:dcps:scim:v1:Base'
};

// Field mappings for each entity type
export const ENTITY_FIELDS: Record<EntityType, string[]> = {
    'Person': [
        // Person includes all fields from BaseScimUser - comprehensive schema
        'AcademicRole', 'AccountName', 'Address', 'Apt', 'BuildingAssistant',
        'BuildingManager', 'CardData', 'City', 'Classes', 'DisplayName', 'Dob',
        'Email', 'EmployeeNumber', 'EndDate', 'EntityType', 'EntraId', 'FirstName',
        'GradeLevel', 'GradeLevels', 'Guardians', 'Id', 'JobCode', 'JobTitle',
        'LastName', 'Manager', 'Manages', 'MemberOf', 'MemberOfTransitive',
        'MobilePhone', 'OfficePhone', 'OnPremId', 'PerpetualAccess', 'PersonalEmail',
        'PhoneIsVerified', 'PhoneLastVerified', 'PhoneNumberVerified', 'PrincipalOf',
        'Rc', 'Rcs', 'Role', 'SapUserId', 'School', 'SchoolName', 'SchoolPrincipalOf',
        'Schools', 'SecondaryEmail', 'Sections', 'SendSms', 'Sponsor', 'Sponsors',
        'StartDate', 'State', 'Students', 'Teachers', 'Type', 'UserPrincipalName',
        'Version', 'WeekendAccess', 'WorkdayAccess', 'Zip'
    ],
    'Student': [
        'AccountName', 'CardData', 'Classes', 'Dob', 'Email', 'EndDate', 'EntraId',
        'FirstName', 'GradeLevel', 'Guardians', 'Id', 'LastName', 'MemberOf',
        'MemberOfTransitive', 'OnPremId', 'Rc', 'Rcs', 'School', 'SchoolName',
        'Schools', 'Sections', 'StartDate', 'Teachers', 'Type', 'UserPrincipalName'
    ],
    'Contractor': [
        'AcademicRole', 'AccountName', 'CardData', 'Classes', 'Dob', 'Email',
        'EmployeeNumber', 'EntraId', 'FirstName', 'GradeLevel', 'GradeLevels',
        'Id', 'JobCode', 'JobTitle', 'LastName', 'Manager', 'MemberOf',
        'MemberOfTransitive', 'OnPremId', 'PerpetualAccess', 'PersonalEmail',
        'PrincipalOf', 'Rc', 'Rcs', 'Role', 'SapUserId', 'School',
        'SchoolPrincipalOf', 'Schools', 'Sections', 'Sponsor', 'Type',
        'UserPrincipalName', 'WeekendAccess', 'WorkdayAccess'
    ],
    'Public': [
        'AccountName', 'Address', 'Apt', 'City', 'Classes', 'Email', 'EntraId',
        'FirstName', 'GradeLevels', 'Id', 'LastName', 'MemberOf',
        'MemberOfTransitive', 'MobilePhone', 'OnPremId', 'PhoneIsVerified',
        'PhoneLastVerified', 'PhoneNumberVerified', 'Rcs', 'Schools', 'Sections',
        'SendSms', 'State', 'Students', 'Teachers', 'UserPrincipalName', 'Zip',
        'SecondaryEmail'
    ],
    'Shared': [
        'AcademicRole', 'AccountName', 'CardData', 'Classes', 'Email', 'EntraId',
        'FirstName', 'GradeLevel', 'GradeLevels', 'Id', 'JobCode', 'JobTitle',
        'LastName', 'Manager', 'MemberOf', 'MemberOfTransitive', 'OnPremId',
        'Rc', 'Rcs', 'Role', 'School', 'Schools', 'Sections', 'Sponsor',
        'Type', 'UserPrincipalName', 'EmployeeNumber', 'SapUserId', 'OfficePhone'
    ],
    'Employee': [
        'AcademicRole', 'AccountName', 'CardData', 'Classes', 'Email', 'EntraId',
        'FirstName', 'GradeLevel', 'GradeLevels', 'Id', 'JobCode', 'JobTitle',
        'LastName', 'Manager', 'MemberOf', 'MemberOfTransitive', 'OnPremId',
        'PerpetualAccess', 'PersonalEmail', 'Rc', 'Rcs', 'Role', 'School',
        'Schools', 'Sections', 'Type', 'UserPrincipalName', 'WeekendAccess', 'WorkdayAccess',
        'EmployeeNumber', 'SapUserId', 'OfficePhone', 'BuildingAssistant', 'BuildingManager',
        'Manages', 'PrincipalOf', 'SchoolPrincipalOf', 'Sponsors'
    ],
    'Sponsored': [
        'AcademicRole', 'AccountName', 'CardData', 'Classes', 'Dob', 'Email', 'EntraId',
        'FirstName', 'GradeLevel', 'GradeLevels', 'Id', 'JobCode', 'JobTitle',
        'LastName', 'Manager', 'MemberOf', 'MemberOfTransitive', 'OnPremId',
        'PerpetualAccess', 'PersonalEmail', 'PrincipalOf', 'Rc', 'Rcs', 'Role',
        'SapUserId', 'School', 'SchoolPrincipalOf', 'Schools', 'Sections',
        'Sponsor', 'Type', 'UserPrincipalName', 'WeekendAccess', 'WorkdayAccess'
    ],

    'Applicant': [
        'AcademicRole', 'AccountName', 'Classes', 'Email', 'EntraId',
        'FirstName', 'GradeLevel', 'GradeLevels', 'Id', 'LastName',
        'MemberOf', 'MemberOfTransitive', 'OnPremId', 'Rc', 'Rcs',
        'School', 'Schools', 'Sections', 'Type', 'UserPrincipalName'
    ],
    'User': [
        'AcademicRole', 'AccountName', 'Address', 'Apt', 'BuildingAssistant',
        'BuildingManager', 'CardData', 'City', 'Classes', 'Dob', 'Email',
        'EmployeeNumber', 'EndDate', 'EntraId', 'FirstName', 'GradeLevel',
        'GradeLevels', 'Guardians', 'Id', 'JobCode', 'JobTitle', 'LastName',
        'Manager', 'Manages', 'MemberOf', 'MemberOfTransitive', 'MobilePhone',
        'OfficePhone', 'OnPremId', 'PerpetualAccess', 'PersonalEmail',
        'PhoneIsVerified', 'PhoneLastVerified', 'PhoneNumberVerified',
        'PrincipalOf', 'Rc', 'Rcs', 'Role', 'SapUserId', 'School',
        'SchoolName', 'SchoolPrincipalOf', 'Schools', 'SecondaryEmail',
        'Sections', 'SendSms', 'Sponsor', 'Sponsors', 'StartDate', 'State',
        'Students', 'Teachers', 'Type', 'UserPrincipalName', 'WeekendAccess',
        'WorkdayAccess', 'Zip'
    ],
    'Vacancy': [
        'AcademicRole', 'AccountName', 'Classes', 'Dob', 'Email', 'EntraId',
        'FirstName', 'GradeLevel', 'GradeLevels', 'Id', 'JobCode', 'JobTitle',
        'LastName', 'Manager', 'MemberOf', 'MemberOfTransitive', 'OnPremId',
        'Rc', 'Rcs', 'Role', 'School', 'Schools', 'Sections', 'Sponsor',
        'Type', 'UserPrincipalName'
    ],
    'Group': [
        'AccountName', 'Description', 'DisplayName', 'Email', 'EntraId',
        'Id', 'Member', 'MemberOf', 'MemberTransitive', 'OnPremId',
        'Scope', 'Type'
    ],
    'School': [
        'Address', 'Building', 'City', 'DisplayName', 'GradeLevels',
        'Id', 'PhoneNumber', 'Principals', 'Rc', 'Region', 'RegionLabel',
        'Role', 'State', 'Type', 'Zip'
    ],
    'Building': [
        'Address', 'Assistant', 'BackupAssistant', 'BackupManager', 'City',
        'District', 'DisplayName', 'Id', 'Manager', 'PerpetualAccess',
        'Rc', 'School', 'State', 'WeekendAccess', 'WorkdayAccess', 'Zip'
    ],
    'Class': [
        'Attendance', 'Course', 'CoursePeriods', 'DisplayName', 'EndDate',
        'Gpa', 'GradeLevels', 'Grades', 'Guardians', 'Id', 'Labels',
        'MarkingPeriods', 'Period', 'Rc', 'Room', 'School', 'SchoolName',
        'Sections', 'StartDate', 'StudentCount', 'Students', 'Subject',
        'Subjects', 'Teacher', 'Teachers', 'Vacant'
    ],
    'ResponsibilityCenter': [
        'Address', 'Building', 'City', 'DisplayName', 'Fax', 'Id', 'Ids',
        'Managers', 'PhoneNumber', 'Principals', 'School', 'State', 'Type', 'Zip'
    ],
    'Image': [
        'Error', 'HasError', 'Hash', 'Id', 'Owner', 'Size', 'Thumbnail'
    ],
    'Base': [
        'Checksum', 'DisplayName', 'Hash', 'Id', 'Recorded', 'VersionId'
    ]
}; 